import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { useSEO, useJSONLD } from '@/hooks/useSEO';
import { features, usageSteps } from '@/mocks/features';
import { faqs, platformStats } from '@/mocks/faqs';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Home() {
  useSEO({
    title: '91.cn com官方入口·正版内容平台导航 - 完整访问指南与功能介绍',
    description: '91.cn com是广受关注的网络内容平台，本页为您提供91.cn com的完整访问指南、功能介绍、安全使用建议及常见问题解答，帮助用户快速了解91.cn com的核心功能与合规使用方式。',
    keywords: '91.cn com,91.cn com官网入口,91.cn com使用指南,91.cn com平台介绍,91.cn com访问方法,91.cn com功能解析,91.cn com常见问题',
    canonicalUrl: 'https://91cn.com',
  });

  useJSONLD({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '91.cn com 官方入口',
    url: 'https://91cn.com',
    description: '91.cn com正版内容平台导航，提供平台介绍、使用指南、功能解析及安全建议',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://91cn.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  });

  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const latestArticles = articles.slice(0, 6);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[480px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20modern%20technology%20landscape%20with%20warm%20orange%20and%20cream%20gradient%2C%20flowing%20organic%20shapes%2C%20minimalist%20geometric%20design%20elements%2C%20soft%20ambient%20lighting%2C%20clean%20professional%20aesthetic%2C%20subtle%20data%20visualization%20patterns%2C%20high%20quality%20editorial%20background%20with%20smooth%20color%20transitions&width=1800&height=800&seq=hero-bg-91cn&orientation=landscape"
            alt="91.cn com 平台背景"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/40"></div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-white font-heading mb-5 leading-tight">
              91.cn com 官方入口·正版内容平台导航
            </h1>
            <p className="text-base md:text-lg text-white/85 max-w-2xl mx-auto mb-8 leading-relaxed">
              为您提供91.cn com的完整访问指南、功能介绍、安全使用建议及常见问题解答，帮助您快速了解平台核心功能与合规使用方式
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/category/user-guide" className="px-8 py-3 bg-primary-500 text-white rounded-lg font-medium text-sm hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap">
                查看使用指南
              </Link>
              <Link to="/category/platform-intro" className="px-8 py-3 bg-white/15 text-white border border-white/30 rounded-lg font-medium text-sm hover:bg-white/25 transition-all duration-200 cursor-pointer whitespace-nowrap">
                了解平台介绍
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="w-full py-12 md:py-16 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {platformStats.map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-lg bg-background-100">
                  <i className={`${stat.icon} text-2xl text-primary-500 mb-3 block`}></i>
                  <div className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">{stat.value}</div>
                  <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">内容分类导航</h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">选择您感兴趣的内容分类，深入了解91.cn com平台的方方面面</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group p-6 rounded-lg bg-background-50 hover:bg-primary-50 transition-all duration-200 cursor-pointer block"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-200 transition-colors">
                      <i className={`${cat.icon} text-xl text-primary-600`}></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground-900 font-heading mb-1">{cat.name}</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed mb-2">{cat.description}</p>
                      <span className="text-xs text-primary-600 font-medium">{cat.articleCount} 篇文章</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">平台核心功能</h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">91.cn com提供一系列强大功能，为用户打造卓越的内容消费体验</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature) => (
                <div key={feature.title} className="p-6 rounded-lg bg-background-100 group hover:bg-background-200/70 transition-all duration-200">
                  <div className="w-12 h-12 rounded-lg bg-accent-100 flex items-center justify-center mb-5 group-hover:bg-accent-200 transition-colors">
                    <i className={`${feature.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading">{feature.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Usage Steps Section */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">快速使用指南</h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">四个简单步骤，快速上手91.cn com平台</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {usageSteps.map((step, i) => (
                <div key={step.step} className="relative p-6 rounded-lg bg-background-50 text-center">
                  <div className="w-14 h-14 rounded-full bg-primary-500 text-white flex items-center justify-center mx-auto mb-4">
                    <span className="text-lg font-bold font-heading">{step.step}</span>
                  </div>
                  <i className={`${step.icon} text-2xl text-primary-500 mb-3 block`}></i>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading">{step.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{step.description}</p>
                  {i < usageSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 text-foreground-300">
                      <i className="ri-arrow-right-s-line text-2xl"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles Section */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">最新文章</h2>
                <p className="text-sm text-foreground-500">了解91.cn com平台的最新资讯和使用技巧</p>
              </div>
              <Link to="/category/user-guide" className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap">
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {latestArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group p-5 rounded-lg bg-background-100 hover:bg-background-200/70 transition-all duration-200 cursor-pointer block"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs px-2 py-1 rounded-md bg-accent-100 text-accent-700 font-medium whitespace-nowrap">{article.categoryName}</span>
                    <span className="text-xs text-foreground-400">{article.readTime}</span>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors font-heading line-clamp-2">{article.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">{article.excerpt}</p>
                  <div className="flex items-center gap-2 mt-4 text-xs text-foreground-400">
                    <i className="ri-calendar-line"></i>
                    <span>{article.publishDate}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">常见问题解答</h2>
              <p className="text-sm md:text-base text-foreground-500">关于91.cn com的常见疑问与详细解答</p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="rounded-lg bg-background-50 overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-background-200/50 transition-colors"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">{faq.question}</span>
                    <i className={`text-foreground-400 transition-transform duration-200 flex-shrink-0 ${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-4">准备好开始使用了吗？</h2>
            <p className="text-sm md:text-base text-foreground-500 mb-8 max-w-xl mx-auto">立即访问91.cn com官方入口，开启您的内容探索之旅</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/category/user-guide" className="px-8 py-3 bg-primary-500 text-white rounded-lg font-medium text-sm hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap">
                查看使用指南
              </Link>
              <Link to="/about" className="px-8 py-3 bg-secondary-500 text-white rounded-lg font-medium text-sm hover:bg-secondary-600 transition-all duration-200 cursor-pointer whitespace-nowrap">
                关于我们
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}